import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useRuntimeVariables } from 'app-kit'
import { LucideIcon } from 'components/LucideIcon'
import { useGeckoAuth } from 'features/auth'
import { EmailSupport } from 'features/navbar/EmailSupport'
import { NavbarButtonContainer } from 'features/navbar/NavbarButtonContainer'
import {
  StyledDropdownMenuContent,
  StyledDropdownMenuTrigger,
} from 'features/navbar/NavbarDropdown'
import { PermissionGateToggle } from 'features/navbar/PermissionGateToggle'
import { ThemeModeToggle } from 'features/navbar/ThemeModeToggle'
import { UnitsToggle } from 'features/navbar/UnitsToggle'
import { PermissionsGate } from 'features/permissions'
import mixpanel from 'mixpanel-browser'
import React from 'react'
import styled from 'styled-components'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import { ProfileAndSettingsBase } from './ProfileAndSettingsBase'
import { type ProfileAndSettingsProps } from './useProfileAndSettingsState'

const ProfilePicFallback = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  width: 18px;
`

const DropdownNonLink = styled(DropdownMenu.Item)`
  padding: 0.25rem 1rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.secondaryText};

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const LogOutButton: React.FC = () => {
  const { logout } = useGeckoAuth()
  const { BASE_PATH } = useRuntimeVariables()
  return (
    <DropdownMenu.Item
      onClick={() => {
        mixpanel.reset()
        logout({
          returnTo: `${window.location.origin}${BASE_PATH}`,
        })
      }}>
      <div style={{ padding: '0.25rem 1rem', cursor: 'pointer' }}>
        <a>Log out</a>
      </div>
    </DropdownMenu.Item>
  )
}

export const ProfileAndSettingsTopBar: React.FC<ProfileAndSettingsProps> = ({
  onOpenNotificationPreferences,
  showNotificationPreferences = true,
}) => (
  <ProfileAndSettingsBase
    renderTrigger={(state) => (
      <StyledDropdownMenuTrigger asChild>
        <NavbarButtonContainer
          $isSelected={state.isModalOpen}
          tabIndex={0}
          role='button'
          aria-label='Open profile settings menu'
          aria-expanded={state.isModalOpen}
          onClick={() => state.setIsModalOpen((open) => !open)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              state.setIsModalOpen((open) => !open)
            }
          }}>
          <ProfilePicFallback>
            <LucideIcon name='User' size={18} />
          </ProfilePicFallback>
        </NavbarButtonContainer>
      </StyledDropdownMenuTrigger>
    )}
    renderContent={(state) => (
      <DropdownMenu.Portal>
        <StyledDropdownMenuContent sideOffset={12} align='end'>
          {state.shouldShowUnits && <UnitsToggle />}
          <ThemeModeToggle />
          {showNotificationPreferences ? (
            <DropdownNonLink onClick={onOpenNotificationPreferences}>
              Manage Notifications
            </DropdownNonLink>
          ) : null}
          <DropdownNonLink onClick={() => state.setShowToSModal(true)}>
            Terms of Service
          </DropdownNonLink>
          <DropdownMenu.Item>
            <EmailSupport />
          </DropdownMenu.Item>
          <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
            <PermissionGateToggle />
          </PermissionsGate>
          <LogOutButton />
        </StyledDropdownMenuContent>
      </DropdownMenu.Portal>
    )}
  />
)
